import { useMemo, useState } from "react";
import { useEntityDelete } from "@pericles/framework-frontend";

import { DeleteButton } from "./DeleteButton";

interface EntityDeleteButtonProps {
  entityName: string;
  entityId: string | number;
  confirmMessage?: string;
  label?: string;
  size?: "sm" | "md";
  onDeleted?: () => void;
  className?: string;
}

export function EntityDeleteButton({
  entityName,
  entityId,
  confirmMessage,
  label,
  size = "sm",
  onDeleted,
  className,
}: EntityDeleteButtonProps): JSX.Element {
  const [error, setError] = useState<string | null>(null);

  const deleteMutation = useEntityDelete({
    entity: entityName,
    id: entityId,
    onSuccess: () => {
      setError(null);
      onDeleted?.();
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : "Failed to delete item";
      setError(message);
    },
  });

  const errorMessage = useMemo(() => {
    if (error) return error;
    if (!deleteMutation.error) return null;
    return deleteMutation.error instanceof Error
      ? deleteMutation.error.message
      : String(deleteMutation.error);
  }, [deleteMutation.error, error]);

  return (
    <DeleteButton
      onDelete={() => deleteMutation.mutate()}
      confirmMessage={confirmMessage}
      label={label}
      size={size}
      isLoading={deleteMutation.isLoading}
      errorMessage={errorMessage}
      onClearError={() => setError(null)}
      className={className}
    />
  );
}
